import { Module } from 'lib/vendor/terrific/Module';
import { Hook } from 'lib/vendor/terrific/Sandbox';

import { DEFAULT_PRICE_REPRESENTATION as DefaultPriceSuffix } from './Price';
import { Product } from './Product';
import { IRetentionChangeData } from './Tc.Module.RetentionResult';
import { Case } from './Subscription';

import {
	ExposedProductInfo,
	ListenerModule,
	simpleSubscribe,
	SUBSCRIPTION_TYPES,
	Texts,
} from './Tc.Module.EcommerceProductDataHub';

export interface ISimplePayChangeData {
	simplePayChecked : boolean;
	simplePayDuration : string | number;
}

enum PLACEHOLDERS {
	originalPrice = 'originalPrice',
	discountedPrice = 'discountedPrice',
	minimumPrice = 'minimumPrice',
	currentPrice = 'currentPrice',
	priceInstead = 'priceInstead',
	simplePayMonthlyPrice = 'simplePayMonthlyPrice',
	withSubscription = 'withSubscription',
}

const PLACEHOLDER_AWARE_ELEMENTS = [
	'.price',
	'.price-label',
];

export default class PriceBox extends Module implements ListenerModule {
	public previousPlaceholders : Texts = {};
	public placeholders : Texts = {};
	private rebatePriceText! : string;
	private withSubscription! : string;
	private withYourSubscription! : string;
	private product! : Product;
	private subscriptionId! : string | undefined;
	private subscriptionDuration! : string | number | undefined;
	private subscriptionType! : SUBSCRIPTION_TYPES;
	private retentionSubscription = '';
	private $footnoteStandard! : JQuery;
	private $footnoteRetention! : JQuery;
	private $simplePayCheckbox! : JQuery;
	private $firstColumn! : JQuery;
	private $secondColumn! : JQuery;

	public on(callback : Hook) {
		const useDynamicPrice = this.$ctx.data('use-dynamic-price');
		this.rebatePriceText = this.$ctx.data('price-rebate-text');
		this.withSubscription = this.$ctx.data('with-subscription');
		this.withYourSubscription = this.$ctx.data('with-your-subscription');
		this.$footnoteStandard = this.$ctx.closest('.gridlayout').find('.footnote-standard');
		this.$footnoteRetention = this.$ctx.closest('.gridlayout').find('.footnote-retention');
		this.$simplePayCheckbox = this.$ctx.find('input[name=simplepay]');
		this.$firstColumn = this.$ctx.find('.first-column');
		this.$secondColumn = this.$ctx.find('.second-column');

		if (useDynamicPrice) {
			// tslint:disable-next-line:no-unbound-method
			simpleSubscribe(this, this.getInfoCallback);
		}

		this.$simplePayCheckbox.change(this.updateSimplePayState.bind(this));
		if (/\bsimplepay=true\b/.test(location.hash)) {
			this.$simplePayCheckbox.prop('checked', true).change();
		}

		callback();
	}

	public getInfoCallback(data : ExposedProductInfo) {
		if (data && data.product && data.subscriptionType) {
			this.log('getInfoCallback', data.subscriptionType);
			this.product = data.product;
			this.subscriptionId = data.subscriptionId || undefined;
			this.subscriptionDuration = data.subscriptionDuration || undefined;
			this.subscriptionType = data.subscriptionType;
			this.updateSimplePayState();
		}
	}

	/**
	 * Listen to and save updates from Tc.Module.RetentionResult
	 */
	public onRetentionChange(data : IRetentionChangeData) {
		if (!data.status || !this.product) {
			return;
		}
		if (data.status === 'possible' && data.existingAboName) {
			this.retentionSubscription = data.existingAboName;
		} else {
			if (this.subscriptionType === SUBSCRIPTION_TYPES.retention) {
				this.retentionSubscription = '';
			}
		}
		this.updatePrices();
		this.log('onRetentionChange', this.subscriptionType, this.retentionSubscription);
	}

	/**
	 * Communicate state change to Tc.Module.PaymentSwitch.
	 */
	protected updateSimplePayState() {
		const simplePayChecked = this.$simplePayCheckbox.prop('checked') as boolean | undefined;
		if (simplePayChecked === undefined) {
			this.log('Simple Pay checkbox not found');

			return;
		}
		const data : ISimplePayChangeData = {
			simplePayChecked,
			simplePayDuration: simplePayChecked && this.subscriptionDuration || '',
		};
		this.log('updateSimplePayState', data);
		this.fire('simplePayChange', data);
		this.updatePrices();
	}

	protected updatePrices() {
		if (!this.product) {
			this.log('no product');

			return;
		}

		// MinimumPrice, originalPrice and discountedPrice (for phones, tablets, watches, and other devices with subscription)
		// With subscription
		this.placeholders[PLACEHOLDERS.minimumPrice] = this.product.getMinimumPrice().formatted(DefaultPriceSuffix, true);
		const originalPrice = this.product.getOriginalPrice().formatted();
		// Device only, full price
		this.placeholders[PLACEHOLDERS.originalPrice] = originalPrice;
		// Device only, sale price
		this.placeholders[PLACEHOLDERS.discountedPrice] = this.product.getDiscountedPrice().formatted();

		// CurrentPrice (for accessories that cannot be combined with a subscription)
		// Device only, sale price
		this.placeholders[PLACEHOLDERS.currentPrice] = this.product.getMinimumPrice().formatted(DefaultPriceSuffix, false);

		// PriceInstead (a.k.a. as crossed-out price)
		if (this.product.isDiscounted()) {
			this.placeholders[PLACEHOLDERS.priceInstead] = this.rebatePriceText.replace(/\{0}/g, originalPrice);
		} else {
			this.placeholders[PLACEHOLDERS.priceInstead] = '';
		}

		// SimplePayMonthlyPrice for watches, tablets, etc
		if (this.$simplePayCheckbox.length) {
			const simplePayMonthlyPrice = this.product.getSimplePayMonthlyPrice(this.subscriptionId, this.subscriptionDuration);
			if (simplePayMonthlyPrice === -1 || !simplePayMonthlyPrice.value()) {
				// SimplePay not possible
				this.placeholders[PLACEHOLDERS.simplePayMonthlyPrice] = DefaultPriceSuffix;
				this.$simplePayCheckbox.prop('disabled', true);
				this.$simplePayCheckbox.prop('checked', false);
				const data : ISimplePayChangeData = {
					simplePayChecked: false,
					simplePayDuration: '',
				};
				this.fire('simplePayChange', data);
			} else {
				this.placeholders[PLACEHOLDERS.simplePayMonthlyPrice] = simplePayMonthlyPrice.formatted();
				this.$simplePayCheckbox.prop('disabled', false);
			}
			const disableSimplePay = !this.$simplePayCheckbox.prop('checked') || this.$simplePayCheckbox.prop('disabled');
			this.$firstColumn.toggleClass('disabled', !disableSimplePay);
			this.$secondColumn.toggleClass('disabled', disableSimplePay);
			this.log('disableSimplePay', disableSimplePay);
		}

		// Retention subscription name and price
		this.$footnoteRetention.hide();
		this.$footnoteStandard.show();
		this.placeholders[PLACEHOLDERS.withSubscription] = this.withSubscription;
		if (this.subscriptionType === SUBSCRIPTION_TYPES.retention && this.retentionSubscription) {
			const personalizedSub = this.product.getSubscriptions().getByName(this.retentionSubscription, 24, Case.Retention);
			if (personalizedSub) {
				const personalizedPrice = personalizedSub.getPrice().formatted(DefaultPriceSuffix, true);
				if (personalizedPrice) {
					this.placeholders[PLACEHOLDERS.withSubscription] = `${this.withYourSubscription} ${this.retentionSubscription}`;
					this.placeholders[PLACEHOLDERS.minimumPrice] = personalizedPrice;
					this.$footnoteStandard.hide();
					this.$footnoteRetention.show();
				}
			}
		}

		this.updatePlaceholders(this.placeholders);
	}

	protected updatePlaceholders(placeholders : Texts, replaceAll = false) : void {
		if(!replaceAll) {
			placeholders = $.extend({}, this.previousPlaceholders || {}, this.placeholders);
			this.previousPlaceholders = placeholders;
		}

		const $elements = $(PLACEHOLDER_AWARE_ELEMENTS.join(', '), this.$ctx);

		$elements.each(function() {
			const element = $(this);

			let elementText = element.data('placeholder-template');
			if (!elementText) {
				elementText = element.text();
				element.data('placeholder-template', elementText);
			}

			element.text(replacePlaceholders(elementText, placeholders));
		});

		this.$ctx.css('opacity', 1);
	}
}

export function replacePlaceholders(source : string, placeholders : Texts) : string {
	return source.replace(/\$\{([a-zA-Z0-9]+)\}/g, (_, placeholderName : string) => placeholders[placeholderName] || '');
}



// WEBPACK FOOTER //
// typescript://../clientlibs/js/lib/ecommerce/Tc.Module.PriceBox.ts